import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  desconectarSesion,
  haceCuanto,
  sesionesPlataforma,
  type SesionConectada,
} from '../../lib/api-sesiones';
import { Boton, CampoLista, Campo, EncabezadoTabla, Esqueleto, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, TextoAyuda } from '../../ui/base';

const EQUIPO = 'equipo';

/**
 * Las sesiones activas de toda la plataforma: los miembros de cada negocio y el equipo. La última
 * renovación es la del token, que ocurre con el uso — la pantalla no finge un «en línea».
 */
export function Sesiones({ puedeEscribir }: { puedeEscribir: boolean }) {
  const [filas, setFilas] = useState<SesionConectada[] | null>(null);
  const [filtro, setFiltro] = useState('');
  const [error, setError] = useState('');
  const [aDesconectar, setADesconectar] = useState<SesionConectada | null>(null);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    sesionesPlataforma()
      .then(setFilas)
      .catch((e) => {
        setFilas([]);
        setError(e instanceof Error ? e.message : 'No pudimos leer las sesiones.');
      });
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const negocios = useMemo(
    () =>
      [...new Map((filas ?? []).filter((s) => s.negocioId).map((s) => [s.negocioId as string, s.negocio ?? ''])).entries()].sort(
        (a, b) => a[1].localeCompare(b[1])
      ),
    [filas]
  );
  const visibles = (filas ?? []).filter((s) =>
    !filtro ? true : filtro === EQUIPO ? s.negocioId === null : s.negocioId === filtro
  );

  function desconectar() {
    if (!aDesconectar || !motivo.trim()) return;
    setOcupado(true);
    setError('');
    desconectarSesion(aDesconectar.sesionId, motivo.trim())
      .then(() => {
        setADesconectar(null);
        setMotivo('');
        cargar();
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'No pudimos desconectar la sesión.'))
      .finally(() => setOcupado(false));
  }

  return (
    <Tarjeta>
      <TextoAyuda>
        Sesiones activas de los miembros de cada negocio y del equipo de plataforma. La última renovación
        ocurre con el uso del dispositivo; al desconectar, sale al refrescar o en menos de una hora.
      </TextoAyuda>

      <div className="mb-3 grid gap-2 sm:grid-cols-4">
        <CampoLista etiqueta="Negocio" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="">Todos</option>
          {negocios.map(([id, nombre]) => (
            <option key={id} value={id}>
              {nombre}
            </option>
          ))}
          <option value={EQUIPO}>Equipo de plataforma</option>
        </CampoLista>
      </div>

      <MensajeError>{error}</MensajeError>

      {filas === null ? (
        <Esqueleto lineas={6} />
      ) : visibles.length === 0 ? (
        <p className="text-muted text-contenido">Sin sesiones activas.</p>
      ) : (
        <TablaAncha>
          <EncabezadoTabla>
            <th className="py-2 pr-2">Negocio</th>
            <th className="py-2 pr-2">Quién</th>
            <th className="py-2 pr-2">Rol</th>
            <th className="py-2 pr-2">Última renovación</th>
            <th className="py-2 pr-2">Dispositivo</th>
            <th className="py-2 pr-2" />
          </EncabezadoTabla>
          <tbody>
            {visibles.map((s) => (
              <FilaTabla key={`${s.sesionId}-${s.negocioId ?? EQUIPO}`} data-e2e="fila-sesion-plataforma">
                <td className="py-2 pr-2 text-contenido">
                  {s.negocio ?? 'Equipo de plataforma'}
                  {s.esSintetico && (
                    <span className="ml-1">
                      <Insignia tono="neutro">DE PRUEBA</Insignia>
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 font-semibold">{s.nombre ?? '—'}</td>
                <td className="py-2 pr-2 text-contenido">{s.rol}</td>
                <td className="py-2 pr-2 text-contenido">
                  {haceCuanto(s.renovadaEn)}
                  {s.esActual && ' (esta sesión)'}
                </td>
                <td className="text-muted py-2 pr-2 text-etiqueta">{s.dispositivo ?? '—'}</td>
                <td className="py-2 pr-2 text-right">
                  {!s.esActual && puedeEscribir && (
                    <Boton
                      variante="secundario"
                      disabled={ocupado}
                      onClick={() => {
                        setADesconectar(s);
                        setMotivo('');
                      }}
                    >
                      Desconectar
                    </Boton>
                  )}
                </td>
              </FilaTabla>
            ))}
          </tbody>
        </TablaAncha>
      )}

      {aDesconectar && (
        <div className="bg-info/10 rounded-control mt-3 px-3 py-2">
          <p className="mb-2 text-contenido font-semibold">
            Desconectar a {aDesconectar.nombre ?? 'este dispositivo'}
            {aDesconectar.negocio ? ` (${aDesconectar.negocio})` : ' (equipo de plataforma)'}: la sesión
            dejará de renovarse; el dispositivo sale al refrescar o en menos de una hora.
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <Campo etiqueta="¿Por qué se desconecta?" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <div className="flex items-end">
              <Boton variante="secundario" disabled={ocupado} onClick={() => setADesconectar(null)}>
                Volver
              </Boton>
            </div>
            <div className="flex items-end">
              <Boton disabled={ocupado || !motivo.trim()} onClick={desconectar}>
                Desconectar
              </Boton>
            </div>
          </div>
        </div>
      )}
    </Tarjeta>
  );
}
